import {
  ActionReducerMapBuilder,
  AsyncThunk,
  createAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { AppRootState, Status } from 'src/store/store'
import { pLimitFiveService } from 'src/v3/utilities/pLimitService'
import {
  AccreditationV2,
  AccreditedAccreditationV2,
  ANCCAccreditation,
  CAPCEAccreditationV2,
  CEBrokerAccreditationV2,
  EventAccreditation,
  EventInvite,
  EventSession,
  EventInviteStatus,
  EventTypeName,
  Instructor,
  LinkedService,
  NewJerseyAccreditationV2,
  NewYorkAccreditationV2,
  NotAccreditedV2,
  QuestionSessionData,
  Syllabus,
  VirginiaAccreditationV2,
} from 'src/v3/core/types'
import { AccreditationsByType } from 'src/v3/events/accreditations'
import {
  getAccreditations,
  getEventAccreditations,
  getEventCertificate,
  getEventProgress,
  getEventQuizQuestions,
  getEventSyllabus,
  getEventsV2,
  getInstructors,
  getLinkedServices,
  getSessionsV3,
} from './utils/api'
import type {
  EventCertificate,
  EventCertificatePoll,
  EventPhase,
  EventProgress,
  EventsTab,
  EventV2,
} from './types'
import { isSessionPast } from './utils/access-window'
import type { RegistrationInfo } from './utils/rsvp-state'
import { deriveEventAttention } from './utils/session-attention'
import type { EventAttention } from './utils/session-attention'

export type HydratedSessionV3 = EventSession & {
  invites: (Pick<EventInvite, 'id' | 'userID'> & {
    status: EventInviteStatus
  })[]
  registrationInfo?: RegistrationInfo
}

type EventsV2State = {
  activeTab: EventsTab
  searchQuery: string
  linkedServiceFilter: string | null
  eventTypeFilter: EventTypeName | 'all'
  dateLowerFilter: string | null
  dateUpperFilter: string | null
  categoryFilter: string | null
  accreditationFilter: string | null
  instructorFilter: string | null
  events: EventV2[]
  eventsStatus: Status
  // The requestId of the most recently dispatched fetchEvents OR refreshEvents. Both write
  // events and can overlap (auto-fetch/Retry alongside post-action refreshes), so only the
  // latest may land; a straggler can't restore stale per-user progress fields.
  latestEventsRequestID: string | null
  sessions: HydratedSessionV3[]
  sessionsStatus: Status
  instructors: Instructor[]
  instructorsStatus: Status
  accreditations: AccreditationV2[]
  accreditationsStatus: Status
  eventAccreditations: EventAccreditation[]
  eventAccreditationsStatus: Status
  linkedServices: LinkedService[]
  linkedServicesStatus: Status
  eventProgress: Record<string, EventProgress>
  eventProgressStatus: Record<string, Status>
  // The requestId of the most recently DISPATCHED fetchEventProgress per event, not the most
  // recently resolved one. Mount, check-in, survey and certificate all refetch, and the detail
  // and quiz pages each fetch on mount, so requests overlap and can resolve out of order.
  // Only the latest dispatched request may settle the status or replace the progress, so a
  // straggler can't roll progress back or flip a fresh pending to rejected (which would also
  // wedge useFreshFetch, since it waits for pending → fulfilled).
  latestProgressRequestID: Record<string, string>
  // The current attempt's quiz questions per event. Guarded by requestId like progress, since
  // the quiz page gates on useFreshFetch.
  eventQuizQuestions: Record<string, QuestionSessionData[]>
  eventQuizQuestionsStatus: Record<string, Status>
  latestQuizQuestionsRequestID: Record<string, string>
  eventCertificatePoll: Record<string, EventCertificatePoll>
  syllabusById: Record<string, Syllabus | null>
  syllabusStatusById: Record<string, Status>
}

export let initialState: EventsV2State = {
  activeTab: 'upcoming',
  searchQuery: '',
  linkedServiceFilter: null,
  eventTypeFilter: 'all',
  dateLowerFilter: null,
  dateUpperFilter: null,
  categoryFilter: null,
  accreditationFilter: null,
  instructorFilter: null,
  events: [],
  eventsStatus: 'uninitialized',
  latestEventsRequestID: null,
  sessions: [],
  sessionsStatus: 'uninitialized',
  instructors: [],
  instructorsStatus: 'uninitialized',
  accreditations: [],
  accreditationsStatus: 'uninitialized',
  eventAccreditations: [],
  eventAccreditationsStatus: 'uninitialized',
  linkedServices: [],
  linkedServicesStatus: 'uninitialized',
  eventProgress: {},
  eventProgressStatus: {},
  latestProgressRequestID: {},
  eventQuizQuestions: {},
  eventQuizQuestionsStatus: {},
  latestQuizQuestionsRequestID: {},
  eventCertificatePoll: {},
  syllabusById: {},
  syllabusStatusById: {},
}

export let fetchEvents = createAsyncThunk('eventsV2/fetchEventsStatus', () =>
  pLimitFiveService(getEventsV2),
)

// Re-fetches events without touching eventsStatus, so pages don't flash skeletons and a
// failed refresh keeps the data already loaded. Used after actions that change the user's
// session progress (check-in, quiz pass, survey submit).
export let refreshEvents = createAsyncThunk('eventsV2/refreshEvents', () =>
  pLimitFiveService(getEventsV2),
)

export let fetchSessions = createAsyncThunk(
  'eventsV2/fetchSessionsStatus',
  () => pLimitFiveService(getSessionsV3),
)

export let fetchInstructors = createAsyncThunk(
  'eventsV2/fetchInstructorsStatus',
  () => pLimitFiveService(getInstructors),
)

export let fetchAccreditations = createAsyncThunk(
  'eventsV2/fetchAccreditationsStatus',
  () => pLimitFiveService(getAccreditations),
)

export let fetchEventAccreditations = createAsyncThunk(
  'eventsV2/fetchEventAccreditationsStatus',
  () => pLimitFiveService(getEventAccreditations),
)

export let fetchLinkedServices = createAsyncThunk(
  'eventsV2/fetchLinkedServicesStatus',
  () => pLimitFiveService(getLinkedServices),
)

export let fetchEventProgress = createAsyncThunk(
  'eventsV2/fetchEventProgress',
  (eventId: string) => getEventProgress(eventId),
)

export let fetchEventQuizQuestions = createAsyncThunk(
  'eventsV2/fetchEventQuizQuestions',
  (eventId: string) => getEventQuizQuestions(eventId),
)

export let fetchSyllabus = createAsyncThunk(
  'eventsV2/fetchSyllabus',
  (eventId: string) => getEventSyllabus(eventId),
)

export let pollEventCertificate = createAsyncThunk(
  'eventsV2/pollEventCertificate',
  (eventId: string) => getEventCertificate(eventId),
)

export let eventCertificatePollRequested = createAction<string>(
  'eventsV2/eventCertificatePollRequested',
)

export let eventCertificatePollExhausted = createAction<string>(
  'eventsV2/eventCertificatePollExhausted',
)

export let eventCertificatePollCancelled = createAction<string>(
  'eventsV2/eventCertificatePollCancelled',
)

type StatusKeys<T> = {
  [K in keyof T]: T[K] extends Status ? K : never
}[keyof T]
type DataKeys<T> = Exclude<keyof T, StatusKeys<T>>

let addAsyncCases = <
  D extends DataKeys<EventsV2State>,
  S extends StatusKeys<EventsV2State>,
>(
  builder: ActionReducerMapBuilder<EventsV2State>,
  thunk: AsyncThunk<EventsV2State[D], any, any>,
  dataKey: D,
  statusKey: S,
) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[statusKey] = 'pending' as Status
    })
    .addCase(thunk.rejected, (state) => {
      state[statusKey] = 'rejected' as Status
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[statusKey] = 'fulfilled' as Status
      state[dataKey] = action.payload as EventsV2State[D]
    })
}

// fetchEvents and refreshEvents settle the same way: only the latest dispatched request lands.
let settleEvents = (
  state: EventsV2State,
  action: { payload: EventV2[]; meta: { requestId: string } },
) => {
  if (state.latestEventsRequestID !== action.meta.requestId) return
  state.eventsStatus = 'fulfilled'
  state.events = action.payload
}

export let eventsV2Slice = createSlice({
  name: 'eventsV2',
  initialState,
  reducers: {
    activeTabChanged: (state, action: PayloadAction<EventsTab>) => {
      state.activeTab = action.payload
    },
    searchQueryChanged: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    linkedServiceFilterChanged: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.linkedServiceFilter = action.payload
      state.instructorFilter = null
    },
    instructorFilterChanged: (state, action: PayloadAction<string | null>) => {
      state.instructorFilter = action.payload
    },
    eventTypeFilterChanged: (
      state,
      action: PayloadAction<EventTypeName | 'all'>,
    ) => {
      state.eventTypeFilter = action.payload
    },
    dateLowerFilterChanged: (state, action: PayloadAction<string | null>) => {
      state.dateLowerFilter = action.payload
    },
    dateUpperFilterChanged: (state, action: PayloadAction<string | null>) => {
      state.dateUpperFilter = action.payload
    },
    categoryFilterChanged: (state, action: PayloadAction<string | null>) => {
      state.categoryFilter = action.payload
    },
    accreditationFilterChanged: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.accreditationFilter = action.payload
    },
    filtersReset: (state) => {
      state.searchQuery = ''
      state.linkedServiceFilter = null
      state.eventTypeFilter = 'all'
      state.dateLowerFilter = null
      state.dateUpperFilter = null
      state.categoryFilter = null
      state.accreditationFilter = null
      state.instructorFilter = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state, action) => {
        state.eventsStatus = 'pending'
        state.latestEventsRequestID = action.meta.requestId
      })
      .addCase(refreshEvents.pending, (state, action) => {
        // Silent: status is untouched so pages don't flash skeletons.
        state.latestEventsRequestID = action.meta.requestId
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        if (state.latestEventsRequestID !== action.meta.requestId) return
        state.eventsStatus = 'rejected'
      })
      .addCase(refreshEvents.rejected, (state, action) => {
        if (state.latestEventsRequestID !== action.meta.requestId) return
        // A failed refresh keeps the events already loaded. But if it superseded an in-flight
        // fetchEvents, that fetch's result is ignored, so this request must settle the status.
        if (state.eventsStatus === 'pending') state.eventsStatus = 'rejected'
      })
      .addCase(fetchEvents.fulfilled, settleEvents)
      .addCase(refreshEvents.fulfilled, settleEvents)
    addAsyncCases(builder, fetchSessions, 'sessions', 'sessionsStatus')
    addAsyncCases(builder, fetchInstructors, 'instructors', 'instructorsStatus')
    addAsyncCases(
      builder,
      fetchAccreditations,
      'accreditations',
      'accreditationsStatus',
    )
    addAsyncCases(
      builder,
      fetchEventAccreditations,
      'eventAccreditations',
      'eventAccreditationsStatus',
    )
    addAsyncCases(
      builder,
      fetchLinkedServices,
      'linkedServices',
      'linkedServicesStatus',
    )
    builder
      .addCase(fetchEventProgress.pending, (state, action) => {
        state.eventProgressStatus[action.meta.arg] = 'pending'
        state.latestProgressRequestID[action.meta.arg] = action.meta.requestId
      })
      .addCase(fetchEventProgress.rejected, (state, action) => {
        // Superseded request: see latestProgressRequestID.
        if (
          state.latestProgressRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.eventProgressStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventProgress.fulfilled, (state, action) => {
        // Superseded request: see latestProgressRequestID.
        if (
          state.latestProgressRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.eventProgressStatus[action.meta.arg] = 'fulfilled'
        state.eventProgress[action.meta.arg] = action.payload
      })
    builder
      .addCase(fetchEventQuizQuestions.pending, (state, action) => {
        state.eventQuizQuestionsStatus[action.meta.arg] = 'pending'
        state.latestQuizQuestionsRequestID[action.meta.arg] =
          action.meta.requestId
      })
      .addCase(fetchEventQuizQuestions.rejected, (state, action) => {
        // Superseded request: see latestProgressRequestID.
        if (
          state.latestQuizQuestionsRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.eventQuizQuestionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchEventQuizQuestions.fulfilled, (state, action) => {
        // Superseded request: see latestProgressRequestID.
        if (
          state.latestQuizQuestionsRequestID[action.meta.arg] !==
          action.meta.requestId
        ) {
          return
        }
        state.eventQuizQuestionsStatus[action.meta.arg] = 'fulfilled'
        state.eventQuizQuestions[action.meta.arg] = action.payload
      })
    builder
      .addCase(fetchSyllabus.pending, (state, action) => {
        state.syllabusStatusById[action.meta.arg] = 'pending'
      })
      .addCase(fetchSyllabus.rejected, (state, action) => {
        state.syllabusStatusById[action.meta.arg] = 'rejected'
      })
      .addCase(fetchSyllabus.fulfilled, (state, action) => {
        state.syllabusStatusById[action.meta.arg] = 'fulfilled'
        state.syllabusById[action.meta.arg] = action.payload
      })
    builder
      .addCase(eventCertificatePollRequested, (state, action) => {
        let id = action.payload
        state.eventCertificatePoll[id] = {
          state: 'polling',
          last: null,
          attempts: 0,
        }
      })
      .addCase(pollEventCertificate.pending, (state, action) => {
        let id = action.meta.arg
        let prev = state.eventCertificatePoll[id]
        state.eventCertificatePoll[id] = {
          state: 'polling',
          last:
            prev?.state === 'answered'
              ? prev.certificate
              : (prev?.last ?? null),
          attempts: prev?.attempts ?? 0,
        }
      })
      .addCase(pollEventCertificate.fulfilled, (state, action) => {
        let id = action.meta.arg
        let prev = state.eventCertificatePoll[id]
        if (prev == null) return
        state.eventCertificatePoll[id] = {
          state: 'answered',
          certificate: action.payload,
          attempts: prev.attempts + 1,
        }
      })
      .addCase(pollEventCertificate.rejected, (state, action) => {
        let id = action.meta.arg
        let prev = state.eventCertificatePoll[id]
        if (prev == null) return
        let last: EventCertificate | null =
          prev.state === 'answered' ? prev.certificate : (prev.last ?? null)
        state.eventCertificatePoll[id] = {
          state: 'failed',
          last,
          attempts: prev.attempts + 1,
        }
      })
      .addCase(eventCertificatePollExhausted, (state, action) => {
        let id = action.payload
        let prev = state.eventCertificatePoll[id]
        let last: EventCertificate | null =
          prev?.state === 'answered' ? prev.certificate : (prev?.last ?? null)
        state.eventCertificatePoll[id] = {
          state: 'failed',
          last,
          attempts: prev?.attempts ?? 0,
        }
      })
      .addCase(eventCertificatePollCancelled, (state, action) => {
        // eslint-disable-next-line fp/no-delete
        delete state.eventCertificatePoll[action.payload]
      })
  },
})

export let {
  activeTabChanged,
  searchQueryChanged,
  linkedServiceFilterChanged,
  eventTypeFilterChanged,
  dateLowerFilterChanged,
  dateUpperFilterChanged,
  categoryFilterChanged,
  accreditationFilterChanged,
  instructorFilterChanged,
  filtersReset,
} = eventsV2Slice.actions

export let selectActiveTab = (state: AppRootState) => state.eventsV2.activeTab
export let selectSearchQuery = (state: AppRootState) =>
  state.eventsV2.searchQuery
export let selectLinkedServiceFilter = (state: AppRootState) =>
  state.eventsV2.linkedServiceFilter
export let selectEventTypeFilter = (state: AppRootState) =>
  state.eventsV2.eventTypeFilter
export let selectDateLowerFilter = (state: AppRootState) =>
  state.eventsV2.dateLowerFilter
export let selectDateUpperFilter = (state: AppRootState) =>
  state.eventsV2.dateUpperFilter
export let selectCategoryFilter = (state: AppRootState) =>
  state.eventsV2.categoryFilter
export let selectAccreditationFilter = (state: AppRootState) =>
  state.eventsV2.accreditationFilter
export let selectInstructorFilter = (state: AppRootState) =>
  state.eventsV2.instructorFilter
export let selectEvents = (state: AppRootState) => state.eventsV2.events
export let selectEventsStatus = (state: AppRootState) =>
  state.eventsV2.eventsStatus
export let selectEventV2ById = (id: string) => (state: AppRootState) =>
  state.eventsV2.events.find((e) => e.id === id)

export let selectSessions = (state: AppRootState) => state.eventsV2.sessions
export let selectSessionsStatus = (state: AppRootState) =>
  state.eventsV2.sessionsStatus
export let selectInstructors = (state: AppRootState) =>
  state.eventsV2.instructors
export let selectInstructorsStatus = (state: AppRootState) =>
  state.eventsV2.instructorsStatus
export let selectLinkedServices = (state: AppRootState) =>
  state.eventsV2.linkedServices
export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.eventsV2.linkedServicesStatus

export let selectEventProgress = (eventId: string) => (state: AppRootState) =>
  state.eventsV2.eventProgress[eventId]

export let selectEventProgressStatus =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.eventProgressStatus[eventId]

export let selectEventQuizQuestions =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.eventQuizQuestions[eventId]

export let selectEventQuizQuestionsStatus =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.eventQuizQuestionsStatus[eventId]

export let selectEventCertificatePoll =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.eventCertificatePoll[eventId] ?? null

export let selectEventPhase =
  (eventId: string) =>
  (state: AppRootState): EventPhase => {
    let progress = state.eventsV2.eventProgress[eventId]
    if (!progress) return null

    if (progress.attendance.kind === 'pending') {
      return {
        phase: 'attendance',
        sessionID: progress.attendance.sessionID,
        sessionName: progress.attendance.sessionName,
        sessionTime: progress.attendance.sessionTime,
        mechanism: progress.attendance.mechanism,
        selfCheckInOpen: progress.attendance.selfCheckInOpen,
      }
    }

    if (progress.attendance.kind === 'notEligible') {
      return null
    }

    if (
      progress.quiz.kind === 'notAttempted' ||
      progress.quiz.kind === 'inProgress' ||
      progress.quiz.kind === 'failed'
    ) {
      return { phase: 'quiz' }
    }

    if (progress.survey.kind === 'notSubmitted') {
      return { phase: 'survey' }
    }

    return null
  }

// Whether the user's current attempt is finished, as recorded by the API.
// Distinguishes "done" from "not eligible", which selectEventPhase both reports as null.
export let selectEventCompleted =
  (eventId: string) =>
  (state: AppRootState): boolean =>
    state.eventsV2.eventProgress[eventId]?.completion.kind === 'completed'

// Issued or pending certificates always show, even alongside a new attempt's step.
// An event with no certificate (no CE hours) shows the card once it's completed,
// to confirm the credit. A notEarned certificate only shows once every step is
// done, so its unmet requirements surface where the certificate would otherwise appear.
export let selectShowEventCertificate =
  (eventId: string) =>
  (state: AppRootState): boolean => {
    let progress = state.eventsV2.eventProgress[eventId]
    if (!progress) return false
    if (progress.certificate.kind === 'notApplicable')
      return progress.completion.kind === 'completed'
    if (progress.certificate.kind !== 'notEarned') return true
    return (
      progress.attendance.kind === 'recorded' &&
      (progress.quiz.kind === 'passed' ||
        progress.quiz.kind === 'notRequired') &&
      progress.survey.kind !== 'notSubmitted'
    )
  }
export let selectSessionById = (id: string) => (state: AppRootState) =>
  state.eventsV2.sessions.find((s) => s.id === id)
export let selectSessionsByEventId = (id: string) => (state: AppRootState) =>
  // eslint-disable-next-line fp/no-mutating-methods
  state.eventsV2.sessions
    .filter((s) => s.eventID === id)
    .sort((a, b) => dayjs(a.sessionTime).diff(b.sessionTime))
    .map((s, i) => ({
      ...s,
      name: s.name || `Session ${i + 1}`,
    }))

export let selectRegisteredSessionsByEventId =
  (id: string) => (state: AppRootState) => {
    // eslint-disable-next-line fp/no-mutating-methods
    let allForEvent = [...state.eventsV2.sessions]
      .filter((s) => s.eventID === id)
      .sort((a, b) => dayjs(a.sessionTime).diff(b.sessionTime))
      .map((s, i) => ({ ...s, name: s.name || `Session ${i + 1}` }))
    return allForEvent.filter((s) => {
      if (!s.invites.some((i) => i.status === 'accepted')) return false
      return !isSessionPast(s.sessionTime, s.endTime)
    })
  }

// Every event that needs something from the user, keyed by event ID. Memoized on events, so
// the index tab and each session row share one derivation.
export let selectEventAttentionByEventId = createSelector(
  [selectEvents],
  (events) =>
    new Map(
      events.flatMap((e): [string, EventAttention][] => {
        let attention = deriveEventAttention(e)
        return attention ? [[e.id, attention]] : []
      }),
    ),
)

export let selectEventAttention =
  (id: string) =>
  (state: AppRootState): EventAttention | null =>
    selectEventAttentionByEventId(state).get(id) ?? null

// null when the API doesn't send the field yet (before ENG-979 deploys), so callers can fall
// back instead of treating it as "nothing completed".
export let selectCompletedSessionIDsByEventId =
  (id: string) =>
  (state: AppRootState): string[] | null =>
    state.eventsV2.events.find((e) => e.id === id)?.completedSessionIDs ?? null

// The session the current attempt's credit goes to. Prefers the API's incompleteSessionID
// (latest attendance recorded); otherwise the latest attended session by start time.
export let selectLatestAttendedSessionByEventId =
  (id: string) =>
  (state: AppRootState): HydratedSessionV3 | undefined => {
    let attended = state.eventsV2.sessions.filter(
      (s) => s.eventID === id && s.invites.some((i) => i.status === 'attended'),
    )
    let incompleteSessionID = state.eventsV2.events.find(
      (e) => e.id === id,
    )?.incompleteSessionID
    return (
      attended.find((s) => s.id === incompleteSessionID) ??
      attended.reduce<HydratedSessionV3 | undefined>(
        (latest, s) =>
          latest == null || dayjs(s.sessionTime).isAfter(latest.sessionTime)
            ? s
            : latest,
        undefined,
      )
    )
  }

export let selectAccreditations = (state: AppRootState) =>
  state.eventsV2.accreditations
export let selectAccreditationsStatus = (state: AppRootState) =>
  state.eventsV2.accreditationsStatus
export let selectEventAccreditations = (state: AppRootState) =>
  state.eventsV2.eventAccreditations
export let selectEventAccreditationsStatus = (state: AppRootState) =>
  state.eventsV2.eventAccreditationsStatus

let groupAccreditationsByType = (
  accreditations: AccreditationV2[],
): AccreditationsByType => ({
  ancc: accreditations.filter((a) => a.kind === 'ancc') as ANCCAccreditation[],
  capce: accreditations.filter(
    (a) => a.kind === 'capce',
  ) as CAPCEAccreditationV2[],
  newJersey: accreditations.filter(
    (a) => a.kind === 'newJersey',
  ) as NewJerseyAccreditationV2[],
  ceBroker: accreditations.filter(
    (a) => a.kind === 'ceBroker',
  ) as CEBrokerAccreditationV2[],
  newYork: accreditations.filter(
    (a) => a.kind === 'newYork',
  ) as NewYorkAccreditationV2[],
  virginia: accreditations.filter(
    (a) => a.kind === 'virginia',
  ) as VirginiaAccreditationV2[],
  accredited: accreditations.filter(
    (a) => a.kind === 'accredited',
  ) as AccreditedAccreditationV2[],
  notAccredited: accreditations.filter(
    (a) => a.kind === 'notAccredited',
  ) as NotAccreditedV2[],
})

export let selectAccreditationsByTypeByEvent =
  (eventID: string) => (state: AppRootState) => {
    let eventAccreditationIDs = new Set(
      state.eventsV2.eventAccreditations
        .filter((ea) => ea.eventID === eventID)
        .map((ea) => ea.accreditationID),
    )
    return groupAccreditationsByType(
      state.eventsV2.accreditations.filter((a) =>
        eventAccreditationIDs.has(a.id),
      ),
    )
  }

export let selectSyllabusByEventId =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.syllabusById[eventId] ?? null

export let selectSyllabusStatusByEventId =
  (eventId: string) => (state: AppRootState) =>
    state.eventsV2.syllabusStatusById[eventId] ?? 'uninitialized'

export let selectFilteredSessions = createSelector(
  [
    selectSessions,
    selectSearchQuery,
    selectLinkedServiceFilter,
    selectEventTypeFilter,
    selectDateLowerFilter,
    selectDateUpperFilter,
    selectCategoryFilter,
    selectAccreditationFilter,
    selectInstructorFilter,
    selectEvents,
    selectEventAccreditations,
    selectAccreditations,
  ],
  (
    sessions,
    search,
    linkedService,
    eventType,
    dateLower,
    dateUpper,
    category,
    accreditation,
    instructorFilter,
    events,
    eventAccreditations,
    accreditations,
  ) => {
    let eventsById = new Map(events.map((e) => [e.id, e]))

    let accreditationKindById = new Map(
      accreditations.map((a) => [a.id, a.kind]),
    )
    let eventAccreditationKinds = eventAccreditations.reduce((map, ea) => {
      let kind = accreditationKindById.get(ea.accreditationID)
      if (kind == null) return map
      let existing = map.get(ea.eventID)
      if (existing != null) {
        existing.add(kind)
      } else {
        map.set(ea.eventID, new Set([kind]))
      }
      return map
    }, new Map<string, Set<string>>())

    let q = search.toLowerCase().trim()
    let dateLowerDay = dateLower != null ? dayjs(dateLower) : null
    let dateUpperDay = dateUpper != null ? dayjs(dateUpper) : null

    return sessions.filter((session) => {
      let event = eventsById.get(session.eventID)
      if (!event) return false

      if (q) {
        let matchesSearch =
          (session.name?.toLowerCase().includes(q) ?? false) ||
          event.name.toLowerCase().includes(q)
        if (!matchesSearch) return false
      }

      if (linkedService != null) {
        if (linkedService === 'FOAMfrat' && event.linkedServiceID != null)
          return false
        if (
          linkedService !== 'FOAMfrat' &&
          event.linkedServiceID !== linkedService
        )
          return false
      }

      if (eventType !== 'all' && event.location.kind !== eventType) return false

      let sessionDay =
        dateLowerDay != null || dateUpperDay != null
          ? dayjs(session.sessionTime)
          : null
      if (dateLowerDay != null && sessionDay!.isBefore(dateLowerDay, 'day'))
        return false
      if (dateUpperDay != null && sessionDay!.isAfter(dateUpperDay, 'day'))
        return false

      if (category != null && event.category?.kind !== category) return false

      if (accreditation != null) {
        let kinds = eventAccreditationKinds.get(event.id)
        if (!kinds?.has(accreditation)) return false
      }

      if (instructorFilter != null) {
        let effectiveIDs = session.instructorIDs?.length
          ? session.instructorIDs
          : (event.instructorIDs ?? [])
        if (!effectiveIDs.includes(instructorFilter)) return false
      }

      return true
    })
  },
)

export let selectInstructorOptions = createSelector(
  [selectEvents, selectSessions, selectLinkedServiceFilter, selectInstructors],
  (events, sessions, linkedServiceFilter, instructors) => {
    let relevantEvents =
      linkedServiceFilter != null
        ? linkedServiceFilter === 'FOAMfrat'
          ? events.filter((e) => e.linkedServiceID == null)
          : events.filter((e) => e.linkedServiceID === linkedServiceFilter)
        : events
    let relevantEventIDs = new Set(relevantEvents.map((e) => e.id))
    let eventInstructorIDs = relevantEvents.flatMap(
      (e) => e.instructorIDs ?? [],
    )
    let sessionInstructorIDs = sessions
      .filter(
        (s) =>
          relevantEventIDs.has(s.eventID) && (s.instructorIDs?.length ?? 0) > 0,
      )
      .flatMap((s) => s.instructorIDs ?? [])
    let instructorIDSet = new Set([
      ...eventInstructorIDs,
      ...sessionInstructorIDs,
    ])
    // eslint-disable-next-line fp/no-mutating-methods
    return instructors
      .filter((i) => instructorIDSet.has(i.id))
      .sort((a, b) => a.name.localeCompare(b.name))
  },
)

export default eventsV2Slice.reducer
